// src/render/confetti.ts
// Win celebration (spec §5.4): two party-popper cannons fire from the bottom corners up and across
// the board; pieces tumble and FLUTTER (horizontal sway + ribbon flip) as gravity pulls them down.
// Self-contained transparent overlay <canvas> over the board; never touches the game canvas or input.
// Honors reduced motion (burst() is a no-op). The loop auto-stops once every piece has expired.

/** The vibrant brick hues, so the burst matches the board. */
const PALETTE: readonly string[] = [
  '#FF3D63',
  '#2E8BFF',
  '#2FCB57',
  '#FFD21F',
  '#B24DFF',
  '#FF8A1E',
  '#FFFFFF',
];

const GRAVITY = 560; // px / s^2 (low — pieces hang + flutter)
const FADE_MS = 500;

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}
const rand = (a: number, b: number): number => a + Math.random() * (b - a);

type Shape = 'ribbon' | 'square' | 'circle';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rot: number;
  vrot: number;
  w: number;
  h: number;
  color: string;
  shape: Shape;
  age: number; // ms
  life: number; // ms remaining
  swayPhase: number;
  swayAmp: number;
  flutter: number;
}

export class Confetti {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D | null;
  private particles: Particle[] = [];
  private rafId: number | null = null;
  private lastTs = 0;
  private disposed = false;

  constructor(parent: HTMLElement) {
    const canvas = document.createElement('canvas');
    canvas.className = 'confetti-overlay';
    canvas.setAttribute('aria-hidden', 'true');
    parent.appendChild(canvas);
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
  }

  /** Fire the celebration. No-op under reduced motion (§5.4). */
  burst(reducedMotion: boolean): void {
    if (this.disposed || reducedMotion || this.ctx === null) {
      return;
    }
    const w = this.canvas.clientWidth;
    const h = this.canvas.clientHeight;
    if (w <= 0 || h <= 0) {
      return;
    }
    const dpr = Math.max(1, Math.min(window.devicePixelRatio || 1, 2));
    this.canvas.width = Math.round(w * dpr);
    this.canvas.height = Math.round(h * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.spawn(w, h);
    if (this.rafId === null) {
      this.lastTs = now();
      this.rafId = requestAnimationFrame((ts) => this.tick(ts));
    }
  }

  dispose(): void {
    this.disposed = true;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.particles = [];
    this.canvas.remove();
  }

  // ---- internals ----

  private spawn(w: number, h: number): void {
    const reach = Math.hypot(w, h);
    // 1) Center pop — explodes outward with a slight upward bias; gravity + flutter pull it down.
    const cx = w * 0.5;
    const cy = h * 0.42;
    for (let i = 0; i < 80; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = reach * rand(0.16, 0.5);
      this.push(cx + rand(-12, 12), cy + rand(-12, 12), Math.cos(angle) * speed, Math.sin(angle) * speed - rand(40, 160));
    }
    // 2) Top curtain — a staggered sheet raining down across the full width (stays in view as it falls).
    for (let i = 0; i < 130; i++) {
      this.push(rand(0, w), rand(-h * 0.6, -8), rand(-60, 60), rand(40, 150));
    }
  }

  private push(x: number, y: number, vx: number, vy: number): void {
    const shape: Shape = Math.random() < 0.6 ? 'ribbon' : Math.random() < 0.6 ? 'square' : 'circle';
    const long = shape === 'ribbon';
    const color = PALETTE[Math.floor(Math.random() * PALETTE.length)] ?? '#FF3D63';
    this.particles.push({
      x,
      y,
      vx,
      vy,
      rot: rand(0, Math.PI * 2),
      vrot: rand(-9, 9),
      w: long ? rand(7, 11) : rand(8, 14),
      h: long ? rand(13, 22) : rand(8, 14),
      color,
      shape,
      age: 0,
      life: rand(2600, 3600),
      swayPhase: rand(0, Math.PI * 2),
      swayAmp: rand(40, 95),
      flutter: rand(0.006, 0.013),
    });
  }

  private tick(ts: number): void {
    if (this.disposed || this.ctx === null) {
      this.rafId = null;
      return;
    }
    const dtMs = Math.min(48, Math.max(0, ts - this.lastTs));
    this.lastTs = ts;
    const dt = dtMs / 1000;
    const ctx = this.ctx;
    const w = this.canvas.clientWidth;
    const h = this.canvas.clientHeight;
    ctx.clearRect(0, 0, w, h);

    const drag = Math.exp(-0.9 * dt);
    const next: Particle[] = [];
    for (const p of this.particles) {
      p.age += dtMs;
      p.life -= dtMs;
      if (p.life <= 0) {
        continue;
      }
      p.vy += GRAVITY * dt;
      p.vx *= drag;
      // Flutter: a sideways sway that grows as the piece slows + falls (paper catching air).
      const sway = Math.sin(p.age * p.flutter + p.swayPhase) * p.swayAmp;
      p.x += (p.vx + sway) * dt;
      p.y += p.vy * dt;
      p.rot += p.vrot * dt;
      if (p.y > h + 60) {
        continue;
      }
      const alpha = p.life < FADE_MS ? p.life / FADE_MS : 1;
      ctx.save();
      ctx.globalAlpha = Math.max(0, Math.min(1, alpha));
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.fillStyle = p.color;
      if (p.shape === 'circle') {
        ctx.beginPath();
        ctx.arc(0, 0, p.w * 0.5, 0, Math.PI * 2);
        ctx.fill();
      } else if (p.shape === 'square') {
        ctx.fillRect(-p.w * 0.5, -p.h * 0.5, p.w, p.h);
      } else {
        // ribbon: flip on its axis (|cos|) so it reads as fluttering paper turning edge-on.
        const flip = Math.abs(Math.cos(p.age * p.flutter * 1.4 + p.swayPhase));
        ctx.scale(1, 0.25 + 0.75 * flip);
        ctx.fillRect(-p.w * 0.5, -p.h * 0.5, p.w, p.h);
      }
      ctx.restore();
      next.push(p);
    }
    this.particles = next;

    if (this.particles.length > 0) {
      this.rafId = requestAnimationFrame((t) => this.tick(t));
    } else {
      ctx.clearRect(0, 0, w, h);
      this.rafId = null;
    }
  }
}
